import moment from 'moment';
import { reportError, setLoading } from './appActions';
import { PARTICIPANTS_LIST_CONTAINER_NAME } from '../../environment';
import {
  FETCH_TRIAL_SUCCESS,
  FETCH_TRIAL_START,
  FETCH_TRIAL_FAIL,
  UPDATE_TRIALS_SURVEY,
  RESTORE_SURVEY_PAYLOAD,
  SET_SURVEY_PAYLOAD_RESTORED,
  SET_YEAR_FILTER,
  SET_TRIAL_LIST,
  TrialActionTypes,
  AppThunk,
  Trial,
  Survey,
  StateTrial,
  SurveyHistoryDiff,
} from '../types';
import {
  loopbackPost,
  loopbackPut,
  loopbackGet,
} from '../../loopback';
import ApiClient from '../../loopback/apiClient';
import { recallSubmissionState } from '.';
import { extendSurveyWithHighlightFlags } from '../../modules/survey/highlight';

export const createTrial = (trialData: Trial): Promise<Trial> => loopbackPost('Trials', trialData);

export const updateTrial = (trial: Trial) => (): Promise<any> => loopbackPut(`trials/${trial.id}`, trial);

export const createBookingsList = (trial: Trial) => (): Promise<any> => loopbackGet(`trials/${trial.id}/createBookingsList`);

export const createParticipantsList = (trial: Trial) => async (dispatch: any): Promise<any> => {
  try {
    const { filename } = await loopbackGet(`trials/${trial.id}/exportParticipants`);
    return {
      container: PARTICIPANTS_LIST_CONTAINER_NAME,
      file: filename,
    };
  } catch (error: any) {
    return dispatch(
      reportError(
        `Error during creating list of participants for trial: ${trial.id}: ${error.message}`,
      ),
    );
  }
};

/**
 * Action creator to set states before start fetching trial
 */
const requestTrial = (): TrialActionTypes => ({
  type: FETCH_TRIAL_START,
});

export const requestTrialSuccess = (trial: StateTrial): TrialActionTypes => ({
  type: FETCH_TRIAL_SUCCESS,
  payload: trial,
});

const requestTrialFail = (): TrialActionTypes => ({
  type: FETCH_TRIAL_FAIL,
});

export const addTrialObjToStore = (
  trialObj: Trial | Trial & {survey: Survey},
): TrialActionTypes => requestTrialSuccess(trialObj);

/**
 * Redux-thunk for async action creator
 * Action creator to fectch trial and set in store
 *
 * @param {number} trialId
 */
export const fetchTrial = (
  trialId: number,
): AppThunk => async (dispatch, getState): Promise<void> => {
  /**
     * getState() returns the root node of the store as in redux/index.js
     * or as we see in redux dev tools. in the future we can change the name
     * so we don't have to write trial.trial to get the trial
     */
  if (getState().trial.isFetching) {
    return;
  }
  dispatch(requestTrial());
  try {
    const trial = await loopbackGet(`Trials/${trialId}`);
    dispatch(requestTrialSuccess(trial));
  } catch (e) {
    dispatch(requestTrialFail());
    dispatch(reportError((e as Error).message));
  }
};

export const fetchTrialAndSurvey = (
  trialId: number,
): AppThunk => async (dispatch, getState): Promise<void> => {
  if (getState().trial.isFetching) {
    return;
  }
  dispatch(requestTrial());
  try {
    dispatch(setLoading(true));
    const trial = await ApiClient.getTrialWithSurvey(trialId, 'de'); // todo : use locale from QuipUser
    dispatch(requestTrialSuccess(trial));
  } catch (e) {
    dispatch(requestTrialFail());
    dispatch(reportError((e as Error).message));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchTrialWithSurveyAndGroundTruth = (
  trialId: number,
): AppThunk => async (dispatch, getState): Promise<void> => {
  if (getState().trial.isFetching) {
    return;
  }
  dispatch(requestTrial());
  try {
    const trial = await ApiClient.getTrialWithSurveyAndGroundTruth(trialId, 'de'); // todo : use locale from QuipUser
    dispatch(requestTrialSuccess(trial));
  } catch (e) {
    dispatch(requestTrialFail());
    dispatch(reportError((e as Error).message));
  }
};

/**
 * This action creator get trial and survey along with enrollment
 * and enrollment submission and put it in store.trial.trial
 *
 * @param {number} trialId
 */
export const fetchTrialWithUserEnrollmentAndSumbission = (
  enrollmentId: number,
): AppThunk => async (dispatch, getState): Promise<void> => {
  if (getState().trial.isFetching) {
    return;
  }
  dispatch(requestTrial());
  try {
    const userId = getState().users?.token?.userId;
    if (!userId) {
      return;
    }
    dispatch(setLoading(true));
    const trial = await ApiClient.getTrialWithSurveyByEnrollmentId(enrollmentId);
    const enrollment = await ApiClient.getEnrollmentWithSubmission(enrollmentId);
    dispatch(setLoading(false));
    if (enrollment.submission && enrollment.submission.payload) {
      dispatch(recallSubmissionState(enrollment.submission.payload));
    }
    const extendedTrial = { ...trial, enrollment } as StateTrial;
    dispatch(requestTrialSuccess(extendedTrial));
  } catch (e) {
    dispatch(requestTrialFail());
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * This action tends to update the survey object inside trial.
 * overwrite: trial.trial.survey object
 *
 * @param {survey} survey
 */
export const updateTrialsSurvey = (survey: Survey): TrialActionTypes => ({
  type: UPDATE_TRIALS_SURVEY,
  payload: survey,
});

/**
 * Restore survey payload copy
 * from trial.surveyPayloadCopy to trial.trial.survey.payload
 */
export const restoreSurveyPayload = (): TrialActionTypes => ({
  type: RESTORE_SURVEY_PAYLOAD,
});

export const setSurveyPayloadRestored = (flag: boolean): TrialActionTypes => ({
  type: SET_SURVEY_PAYLOAD_RESTORED,
  payload: flag,
});

/**
 * Year by which the trials list are being filtered.
 * @param {string} yearUTC
 */
export const setYearFilter = (yearUTC: string): TrialActionTypes => ({
  type: SET_YEAR_FILTER,
  payload: yearUTC,
});

/**
 * Set trials list in trial.trialList
 * @param {Trials[]} trialList
 */
const setTrialList = (trialList: Trial[]): TrialActionTypes => ({
  type: SET_TRIAL_LIST,
  payload: trialList,
});

/**
 * Get trials list based on filtered year.
 * Trial also contains survey and groundtruth.
 * This remote call may only be executed by Admin
 */
export const fetchTrialsList = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const year = getState().trial.yearFilter;
  try {
    const trialLists = await ApiClient.getTrialsList(
      moment().year(Number(year)).startOf('year'),
      moment().year(Number(year)).endOf('year'),
      '*',
    );
    dispatch(setTrialList(trialLists));
  } catch (e) {
    dispatch(reportError((e as Error).message));
  }
};

/**
 * This action cretor extends the trial.survey with trial.extendedSurvey
 */
export const extendSurveyWithHighlight = (
  survey: Survey,
  historyDiff: SurveyHistoryDiff,
): TrialActionTypes => {
  const extendedSurvey = extendSurveyWithHighlightFlags(survey, historyDiff);
  return {
    type: UPDATE_TRIALS_SURVEY,
    payload: extendedSurvey,
  };
};
